import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface UseScrollSpyOptions {
  /** Object mapping section IDs to their subsections for title clicking functionality */
  sectionIds: Record<string, string[]>;
  rootMargin?: string;
  hasManualSelection?: boolean;
}

interface UseScrollSpyReturn {
  activeSection: string;
  setActiveSection?: (sectionId: string) => void;
  handleTitleClick?: (sectionId: string) => void;
}

export const useScrollSpy = ({
  sectionIds,
  rootMargin,
  hasManualSelection = false,
}: UseScrollSpyOptions): string | UseScrollSpyReturn => {
  // Generate arrays from the sectionIds object - memoized to prevent unnecessary re-renders
  const sectionIdsArray = useMemo(
    () =>
      Object.entries(sectionIds).flatMap(([titleId, subsections]) => [
        titleId,
        ...subsections,
      ]),
    [sectionIds],
  );

  // Generate subsections object (only for sections that have subsections) - memoized
  const subsectionIdsArray = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(sectionIds).filter(([, subs]) => subs.length > 0),
      ),
    [sectionIds],
  );

  // state - initialize with first section selected by default
  const [activeSection, setActiveSection] = useState<string>(
    sectionIdsArray[0] || '',
  );
  const [manualSelection, setManualSelection] = useState<string>('');

  // refs
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  // Keep track of which sections are currently visible so we can pick the best one
  const visibleSectionsRef = useRef<Map<string, IntersectionObserverEntry>>(
    new Map(),
  );

  // Use manual selection if available, otherwise use scroll spy
  const finalActiveSection =
    hasManualSelection && manualSelection ? manualSelection : activeSection;

  // handlers
  const updateActiveSection = useCallback(() => {
    const entries = Array.from(visibleSectionsRef.current.values());
    if (entries.length === 0) return;

    // Find the section with the highest intersection ratio
    const bestEntry = entries.reduce(
      (best, entry) => {
        if (!entry.isIntersecting) return best;
        if (!best || entry.intersectionRatio > best.intersectionRatio) {
          return entry;
        }
        return best;
      },
      null as IntersectionObserverEntry | null,
    );

    // Fallback: if no section is intersecting, pick the one closest to the top
    const fallbackEntry =
      bestEntry ||
      entries.reduce((closest, entry) => {
        const currentDistance = Math.abs(entry.boundingClientRect.top);
        const closestDistance = Math.abs(closest.boundingClientRect.top);
        return currentDistance < closestDistance ? entry : closest;
      });

    // Only update if we found a different section
    if (fallbackEntry && fallbackEntry.target.id !== activeSection) {
      setActiveSection(fallbackEntry.target.id);

      // Clear manual selection if user scrolled to a different section
      if (manualSelection && fallbackEntry.target.id !== manualSelection) {
        setManualSelection('');
      }
    }
  }, [activeSection, manualSelection]);

  // Handle manual section selection
  const handleManualSelection = useCallback(
    (sectionId: string) => {
      if (!hasManualSelection) return;
      setManualSelection(sectionId);
      // Clear manual selection after 1 second to allow scroll spy to take over
      setTimeout(() => {
        setManualSelection('');
      }, 1000);
    },
    [hasManualSelection],
  );

  // Handle title clicking - if section has subsections, select first subsection
  const handleTitleClick = useCallback(
    (sectionId: string) => {
      if (!hasManualSelection) return;

      const sectionSubsections = subsectionIdsArray[sectionId];
      if (sectionSubsections && sectionSubsections.length > 0) {
        // If section has subsections, select the first one
        handleManualSelection(sectionSubsections[0]);
      } else {
        // If section has no subsections, select the section itself
        handleManualSelection(sectionId);
      }
    },
    [hasManualSelection, subsectionIdsArray, handleManualSelection],
  );

  // Debounce updates to prevent rapid state changes during scrolling
  const debouncedUpdate = useCallback(() => {
    clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(updateActiveSection, 50);
  }, [updateActiveSection]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Update our visible sections map with current intersection states
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleSectionsRef.current.set(entry.target.id, entry);
          } else {
            visibleSectionsRef.current.delete(entry.target.id);
          }
        });

        // Use debounced update to prevent rapid state changes during scrolling
        debouncedUpdate();
      },
      {
        rootMargin,
        // Multiple thresholds give us more precise intersection data
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
      },
    );

    // Start observing all the sections we want to track
    sectionIdsArray.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    // Cleanup: disconnect observer and clear any pending timeouts
    return () => {
      observer.disconnect();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [sectionIdsArray, rootMargin, debouncedUpdate]);

  // Return different values based on whether manual selection is enabled
  if (hasManualSelection) {
    return {
      activeSection: finalActiveSection,
      setActiveSection: handleManualSelection,
      handleTitleClick,
    };
  }

  return finalActiveSection;
};
